const STATE_ABBR = {
  Wien: "W",
  "Niederösterreich": "NÖ",
  "Oberösterreich": "OÖ",
  Salzburg: "S",
  Tirol: "T",
  Kärnten: "K",
  Steiermark: "Stmk",
  Vorarlberg: "V",
  Burgenland: "B",
  Hessen: "HE",
  Hamburg: "HH",
  Sachsen: "SN",
  Thüringen: "TH",
};

export const MARKET_STATE = {
  // AT
  WIEN01: "Wien", WIEN02: "Wien", WIEN03: "Wien", WIEN04: "Wien", WIEN05: "Wien",
  WIEN06: "Wien", WIEN07: "Wien", WIEN08: "Wien", WIEN09: "Wien", WIEN10: "Wien",
  WIEN11: "Wien", WIEN12: "Wien", WIEN13: "Wien", WIEN14: "Wien", WIEN15: "Wien",
  WIEN16: "Wien", WIEN17: "Wien", WIEN18: "Wien", WIEN19: "Wien", WIEN20: "Wien",
  WIEN21: "Wien", WIEN22: "Wien", WIEN23: "Wien",
  STP: "Niederösterreich", WN: "Niederösterreich", KREMS: "Niederösterreich",
  MOEDLING: "Niederösterreich", KORNEUBURG: "Niederösterreich", BADEN: "Niederösterreich",
  KLU: "Kärnten", VILLACH: "Kärnten", WOLFSBERG: "Kärnten", STVEIT: "Kärnten",
  GRAZ: "Steiermark", GRAZ_UM: "Steiermark", LEOBEN: "Steiermark",
  LINZ: "Oberösterreich", WELS: "Oberösterreich", STEYR: "Oberösterreich",
  SALZBURG: "Salzburg", INNSBRUCK: "Tirol",
  BREGENZ: "Vorarlberg", DORNBIRN: "Vorarlberg",
  KUFSTEIN: "Tirol", TULLN: "Niederösterreich", AMSTETTEN: "Niederösterreich",
  SPITTAL: "Kärnten", FELDKIRCHEN: "Kärnten", HERMAGOR: "Kärnten", STANDRAE: "Kärnten",
  VOECKLABRUCK: "Oberösterreich",
  // DE
  FRANKFURT: "Hessen", HAMBURG: "Hamburg", LEIPZIG: "Sachsen", ERFURT: "Thüringen",
};

export const VIENNA_MARKET_VALUE = "__WIEN__";

function normalizeSearch(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[äÄ]/g, "ae")
    .replace(/[öÖ]/g, "oe")
    .replace(/[üÜ]/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function isViennaMarket(code, name) {
  // name fallback must be exact — a prefix match would fold "Wiener Neustadt"
  // (market WN, Niederösterreich) into the synthetic Wien rollup.
  return String(code ?? "").toUpperCase().startsWith("WIEN") || normalizeSearch(name) === "wien";
}

export function getMarketStateName(code, name) {
  const upper = String(code ?? "").toUpperCase();
  if (upper.startsWith("WIEN")) return "Wien";
  return MARKET_STATE[upper] || (normalizeSearch(name) === "wien" ? "Wien" : null);
}

export function getMarketStateAbbr(code, name) {
  const state = getMarketStateName(code, name);
  return state ? STATE_ABBR[state] || null : null;
}

export function formatMarketLabel(market) {
  const abbr = market.country === "AT" ? getMarketStateAbbr(market.code, market.name) : null;
  return abbr ? `${market.name} [${abbr}]` : market.name;
}

export function buildMarketOptions(listings, countryFilter = "ALL") {
  const base = countryFilter !== "ALL"
    ? listings.filter((l) => l.country === countryFilter)
    : listings;

  const seen = new Map();
  let hasVienna = false;

  base.forEach((listing) => {
    const code = String(listing.market || listing.market_code || listing.code || "").toUpperCase();
    if (!code) return;
    const name = listing.market_name || listing.market || listing.name || code;
    const country = listing.country || "AT";
    const stateName = getMarketStateName(code, name);
    const abbr = country === "AT" ? getMarketStateAbbr(code, name) : null;
    const label = abbr ? `${name} [${abbr}]` : name;
    seen.set(code, {
      value: code,
      code,
      name,
      label,
      country,
      stateName,
      stateAbbr: abbr,
      searchText: normalizeSearch([code, name, stateName, abbr].filter(Boolean).join(" ")),
    });
    if (isViennaMarket(code, name) && country === "AT") hasVienna = true;
  });

  const options = [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  if (hasVienna) {
    options.unshift({
      value: VIENNA_MARKET_VALUE,
      code: VIENNA_MARKET_VALUE,
      name: "Wien",
      label: "Wien [W]",
      country: "AT",
      stateName: "Wien",
      stateAbbr: "W",
      searchText: normalizeSearch("Wien W Wien Vienna Wien districts"),
      synthetic: true,
    });
  }
  return options;
}

export function marketOptionMatchesSelection(listing, selected) {
  if (!selected.length) return true;
  const code = String(listing.market || listing.market_code || listing.code || "").toUpperCase();
  const name = listing.market_name || listing.market || listing.name || "";
  return selected.some((value) => {
    if (value === VIENNA_MARKET_VALUE) return isViennaMarket(code, name);
    return value === code;
  });
}

export function normalizeMarketSearch(value) {
  return normalizeSearch(value);
}
